import { collection, doc, getDoc, getDocs } from 'firebase/firestore'
import { db } from '../firebase-config'
import { buildCanonicalIndices } from './indicesCanonical'
import indicesFixture from '../fixtures/indices-reales.json'

const CACHE_KEY = 'indices_previsionales_cache_v2'

function todayKey() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || parsed.day !== todayKey() || !parsed.data) return null
    return parsed.data
  } catch {
    return null
  }
}

function writeCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ day: todayKey(), data }))
  } catch {
    // ignore cache write errors
  }
}

function normalizeDocs(docs) {
  const out = {}
  const monthlyRows = []
  docs.forEach((doc) => {
    const row = doc.data() || {}
    const id = String(doc.id || '').toUpperCase()
    const periodo = String(row.periodo || '')
    if (/^\d{4}-\d{2}$/.test(periodo) && (row.ripte !== undefined || row.ipc_acumulado !== undefined || row.ipc_mensual !== undefined)) {
      monthlyRows.push({ periodo, ...row })
      return
    }
    const tipo = String(row.tipo || row.indice || id || '').toUpperCase()
    const serie = row.serie && typeof row.serie === 'object' ? row.serie : {}
    if (!tipo) return
    out[tipo] = {
      fuente: row.fuente || 'Firestore indices_previsionales',
      serie,
    }
  })

  if (monthlyRows.length > 0) {
    const ripteSerie = {}
    const ipcSerie = {}
    monthlyRows.forEach((row) => {
      const key = String(row.periodo || '')
      const ripte = Number(row.ripte)
      const ipcAcc = Number(row.ipc_acumulado)
      const ipcMensual = Number(row.ipc_mensual)
      if (Number.isFinite(ripte) && ripte > 0) ripteSerie[key] = ripte
      if (Number.isFinite(ipcAcc) && ipcAcc > 0) ipcSerie[key] = ipcAcc
      else if (Number.isFinite(ipcMensual) && ipcMensual > 0) ipcSerie[key] = ipcMensual
    })

    if (!out.RIPTE && Object.keys(ripteSerie).length > 0) {
      out.RIPTE = {
        fuente: 'Firestore indices_previsionales (monthly rows)',
        serie: ripteSerie,
      }
    }
    if (!out.IPC && Object.keys(ipcSerie).length > 0) {
      out.IPC = {
        fuente: 'Firestore indices_previsionales (monthly rows)',
        serie: ipcSerie,
      }
    }
    if (!out.ISBIC && Object.keys(ripteSerie).length > 0) {
      out.ISBIC = {
        fuente: 'Serie proxy derivada de RIPTE (sin ISBIC oficial cargado)',
        serie: { ...ripteSerie },
      }
    }
  }

  return out
}

function normalizeActiveState(raw) {
  const series = raw && typeof raw.series === 'object' ? raw.series : {}
  const out = {}
  ;['RIPTE', 'IPC', 'ISBIC'].forEach((k) => {
    const serie = series[k] && typeof series[k] === 'object' ? series[k] : {}
    if (Object.keys(serie).length > 0) {
      out[k] = {
        fuente: `indices_active_state:${String(raw.version || 'n/a')}`,
        serie,
      }
    }
  })
  return out
}

function hasCoreSeries(data) {
  if (!data || typeof data !== 'object') return false
  const hasSerie = (k) => data[k] && data[k].serie && Object.keys(data[k].serie).length > 0
  const hasStrict = Boolean(data?.__canonical?.strictCoveragePass)
  return hasSerie('ISBIC') && hasSerie('RIPTE') && hasSerie('IPC') && hasStrict
}

export async function getIndices() {
  const cached = readCache()
  if (cached && hasCoreSeries(cached)) {
    return { success: true, data: cached, source: 'cache' }
  }

  try {
    try {
      const response = await fetch('/api/series/canonical')
      if (response.ok) {
        const payload = await response.json()
        const canonical = payload?.data
        if (canonical && hasCoreSeries(canonical)) {
          writeCache(canonical)
          return { success: true, data: canonical, source: 'api-series-canonical' }
        }
      }
    } catch {
      // fallback to Firestore reads below
    }

    const activeRef = doc(db, 'indices_active_state', 'current')
    const activeSnap = await getDoc(activeRef)
    if (activeSnap.exists()) {
      const normalized = normalizeActiveState(activeSnap.data() || {})
      if (Object.keys(normalized).length > 0) {
        const canonical = buildCanonicalIndices({ ...normalized, __source: 'indices_active_state' })
        if (hasCoreSeries(canonical)) {
          writeCache(canonical)
          return { success: true, data: canonical, source: 'indices_active_state' }
        }
      }
    }

    const snap = await getDocs(collection(db, 'indices_previsionales'))
    const normalized = normalizeDocs(snap.docs)
    const canonical = buildCanonicalIndices({ ...normalized, __source: 'firestore' })
    if (hasCoreSeries(canonical)) {
        writeCache(canonical)
        return { success: true, data: canonical, source: 'firestore' }
    }
    // ── Fixture fallback: when no external source has full coverage, the dense
    //    monthly fixture (390 pts/serie, 1994–2026) guarantees the app always
    //    has usable index data. This is the same data used by tests.
    const fixtureCanonical = buildCanonicalIndices({ ...indicesFixture, __source: 'fixture-fallback' })
    if (hasCoreSeries(fixtureCanonical)) {
      writeCache(fixtureCanonical)
      return { success: true, data: fixtureCanonical, source: 'fixture-fallback' }
    }
    return {
      success: false,
      data: canonical,
      error: new Error('Cobertura estricta de series insuficiente para 2001-2021 (fallback fixture también falló)'),
    }
  } catch (error) {
    return { success: false, data: null, error }
  }
}
